import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { Dashboard } from '@/pages/Dashboard';
import { Login } from '@/pages/Login';
import { OAuthCallback } from '@/pages/OAuthCallback';
import { ImportCSV } from '@/pages/ImportCSV';
import { ManageAccounts } from '@/pages/ManageAccounts';
import { Payables } from '@/pages/Payables';
import { Receivables } from '@/pages/Receivables';
import { Stock } from '@/pages/Stock';
import { CustomerServicePage } from '@/pages/CustomerServicePage';
import { Settings } from '@/pages/Settings';
import { Reconciliation } from '@/pages/Reconciliation';
import { OnboardingChannel } from '@/pages/OnboardingChannel';
import { ManageChannels } from '@/pages/ManageChannels';
import { DREDetailed } from '@/pages/DREDetailed';
import { CategoryMapping } from '@/pages/CategoryMapping';
import { Suppliers } from '@/pages/Suppliers';
import { Tarefas } from '@/pages/Tarefas';
import { TodoFab } from '@/components/todos/TodoFab';
import { ChatFab } from '@/components/chat/ChatFab';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { UpdateNotesModal } from '@/components/UpdateNotesModal';
import { TodosProvider } from '@/context/TodosContext';
import { TermsPage } from '@/pages/TermsPage';
import { ErrorCodesPage } from '@/pages/ErrorCodesPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { FarmsHub } from '@/modules/farms/pages/FarmsHub';
import { Marketplace } from '@/modules/farms/pages/Marketplace';
import { FarmDetail } from '@/modules/farms/pages/FarmDetail';
import { RegisterFarm } from '@/modules/farms/pages/RegisterFarm';
import { MyFarms } from '@/modules/farms/pages/MyFarms';
import { MyOrders } from '@/modules/farms/pages/MyOrders';
import { Checkout } from '@/modules/farms/pages/Checkout';
import { OrderDetail } from '@/modules/farms/pages/OrderDetail';
import { FarmInboxPage } from '@/modules/farms/pages/FarmInboxPage';
import { ChatPanel } from '@/modules/farms/pages/ChatPanel';
import { ChatInbox } from '@/modules/farms/pages/ChatInbox';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-ink-muted">
        Carregando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Redireciona para onboarding se o usuário autenticado ainda não tem tenant
function TenantGuard({ children }: { children: React.ReactNode }) {
  const { tenantId, loading } = useTenant();
  if (loading) return <div className="h-full flex items-center justify-center text-ink-muted">Carregando…</div>;
  if (!tenantId) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

function ProtectedTenantRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <TenantGuard>{children}</TenantGuard>
    </ProtectedRoute>
  );
}

function RootRoute() {
  const [params] = useSearchParams();
  if (params.get('code') || params.get('error')) {
    return <OAuthCallback />;
  }
  return (
    <ProtectedRoute>
      <Navigate to="/farms" replace />
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <TodosProvider>
    <>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/api/ml/callback" element={<OAuthCallback />} />
      <Route path="/" element={<RootRoute />} />

      {/* Financeiro — exige tenant (redireciona para /onboarding se não tiver) */}
      <Route path="/financeiro" element={<ProtectedTenantRoute><Dashboard initialView="financial" /></ProtectedTenantRoute>} />
      <Route path="/financeiro/contas" element={<ProtectedTenantRoute><ManageAccounts /></ProtectedTenantRoute>} />
      <Route path="/financeiro/pagar" element={<ProtectedTenantRoute><Payables /></ProtectedTenantRoute>} />
      <Route path="/financeiro/receber" element={<ProtectedTenantRoute><Receivables /></ProtectedTenantRoute>} />
      <Route path="/financeiro/importar" element={<ProtectedTenantRoute><ImportCSV /></ProtectedTenantRoute>} />
      <Route path="/financeiro/conciliacao" element={<ProtectedTenantRoute><Reconciliation /></ProtectedTenantRoute>} />
      <Route path="/financeiro/dre" element={<ProtectedTenantRoute><DREDetailed /></ProtectedTenantRoute>} />
      <Route path="/configuracoes/categorias"   element={<ProtectedTenantRoute><CategoryMapping /></ProtectedTenantRoute>} />
      <Route path="/configuracoes/fornecedores" element={<ProtectedTenantRoute><Suppliers /></ProtectedTenantRoute>} />
      <Route path="/tarefas" element={<ProtectedTenantRoute><Tarefas /></ProtectedTenantRoute>} />

      {/* Logística */}
      <Route path="/logistica" element={<ProtectedTenantRoute><Dashboard initialView="logistics" /></ProtectedTenantRoute>} />
      <Route path="/logistica/estoque" element={<ProtectedTenantRoute><Stock /></ProtectedTenantRoute>} />
      <Route path="/logistica/atendimento" element={<ProtectedTenantRoute><CustomerServicePage /></ProtectedTenantRoute>} />

      {/* Onboarding — só requer login, não tenant */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingChannel /></ProtectedRoute>} />

      {/* Sistema */}
      <Route path="/configuracoes" element={<ProtectedTenantRoute><Settings /></ProtectedTenantRoute>} />
      <Route path="/configuracoes/canais" element={<ProtectedTenantRoute><ManageChannels /></ProtectedTenantRoute>} />

      {/* MYND Farms */}
      <Route path="/farms"                   element={<ProtectedTenantRoute><FarmsHub /></ProtectedTenantRoute>} />
      <Route path="/farms/marketplace"       element={<ProtectedTenantRoute><Marketplace /></ProtectedTenantRoute>} />
      <Route path="/farms/cadastrar"         element={<ProtectedTenantRoute><RegisterFarm /></ProtectedTenantRoute>} />
      <Route path="/farms/minhas"            element={<ProtectedTenantRoute><MyFarms /></ProtectedTenantRoute>} />
      <Route path="/farms/recebidos"          element={<ProtectedTenantRoute><FarmInboxPage /></ProtectedTenantRoute>} />
      <Route path="/farms/pedidos"            element={<ProtectedTenantRoute><MyOrders /></ProtectedTenantRoute>} />
      <Route path="/farms/pedidos/:orderId"  element={<ProtectedTenantRoute><OrderDetail /></ProtectedTenantRoute>} />
      <Route path="/farms/checkout/:orderId" element={<ProtectedTenantRoute><Checkout /></ProtectedTenantRoute>} />
      <Route path="/farms/conversas"         element={<ProtectedTenantRoute><ChatInbox /></ProtectedTenantRoute>} />
      <Route path="/farms/chat/:threadId"    element={<ProtectedTenantRoute><ChatPanel /></ProtectedTenantRoute>} />
      <Route path="/farms/:slug"             element={<ProtectedTenantRoute><FarmDetail /></ProtectedTenantRoute>} />

      {/* Páginas públicas (sem autenticação) */}
      <Route path="/ajuda/erros" element={<ErrorCodesPage />} />
      <Route path="/termos"      element={<TermsPage />} />
      <Route path="/privacidade" element={<PrivacyPage />} />
      <Route path="/farms/termos" element={<TermsPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <TodoFab />
    <ChatFab />
    <UpdateNotesModal />
    </>
    </TodosProvider>
    </ErrorBoundary>
  );
}
